// src/pages/PrivacyPolicy/PrivacyPolicy.jsx
import React from "react";
import "./PrivacyPolicy.css";
import { useNavigate } from "react-router-dom";
import useScrollToTop from "../../helpers/useScrollToTop";

function PrivacyPolicy() {
  useScrollToTop();
  const navigate = useNavigate();

  const goBack = () => navigate(-1);

  return (
    <div className="privacy-wrapper">
      <div className="privacy-card">
        <h1>Privacy Policy</h1>
        <p className="privacy-updated">Last updated: November 19, 2025</p>

        <p className="privacy-intro">
          At <strong>Little Paws Place™</strong> (“Little Paws Place,” “we,”
          “our,” or “us”), we respect your privacy and are committed to
          protecting the information you share with us. This Privacy Policy
          explains how we collect, use, and protect information when you use our
          website, SmartTags, and related services, including any AI-powered
          tools (together, the “Services”).
        </p>

        <h3>1. Information We Collect</h3>
        <p>We may collect the following types of information:</p>
        <ul>
          <li>
            <strong>Account information:</strong> such as your name, email
            address, and login identifier (for example, a SmartTag ID like
            “LP####”) when you create an account or sign in.
          </li>
          <li>
            <strong>Pet profile information:</strong> such as your pet’s name,
            species, breed, age, and other details you choose to store in your
            SmartTag profile.
          </li>
          <li>
            <strong>Subscription and billing information:</strong> limited
            details related to your subscription status and payments (processed
            through third-party providers such as Stripe). We do not store full
            payment card numbers on our own systems.
          </li>
          <li>
            <strong>Technical and usage data:</strong> such as IP address,
            browser type, device information, pages viewed, and interaction
            patterns with our Services.
          </li>
          <li>
            <strong>Support communications:</strong> messages you send through
            our contact forms, support assistant, or other communication
            channels.
          </li>
        </ul>

        <h3>2. How We Use Your Information</h3>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide and maintain your account and SmartTag services.</li>
          <li>
            Enable AI-driven features (such as setup assistance and basic pet
            support workflows).
          </li>
          <li>
            Operate, secure, and improve the performance and reliability of our
            website and Services.
          </li>
          <li>
            Communicate with you regarding updates, support, billing, and
            important notices related to your account or subscription.
          </li>
          <li>
            Respond to your questions, technical issues, and support requests.
          </li>
        </ul>

        <h3>3. AI Features & Data</h3>
        <p>
          Some parts of our Services may use AI models to help with SmartTag
          setup, pet profile guidance, or customer support. When you interact
          with these features:
        </p>
        <ul>
          <li>
            Your messages and inputs may be processed by AI providers to
            generate responses and improve the quality, safety, and reliability
            of the models.
          </li>
          <li>
            We take steps to avoid using AI responses as a substitute for
            professional veterinary, legal, or emergency guidance.
          </li>
          <li>
            You should avoid sharing sensitive personal information that is not
            necessary for using the Services.
          </li>
        </ul>

        <h3>4. Cookies & Analytics</h3>
        <p>
          We may use cookies, local storage, and similar technologies to
          remember your preferences, keep you signed in, and understand how
          visitors use our Services. We may also use basic analytics tools to
          help us improve performance, user experience, and reliability.
        </p>

        <h3>5. How We Share Information</h3>
        <p>We may share information in the following limited ways:</p>
        <ul>
          <li>
            <strong>Service providers:</strong> with trusted third parties that
            help us operate our Services, such as hosting providers, payment
            processors, email delivery platforms, and analytics providers.
          </li>
          <li>
            <strong>Legal and safety:</strong> when required to comply with
            laws, regulations, legal processes, or to protect the rights,
            property, or safety of Little Paws Place, our users, or others.
          </li>
        </ul>
        <p>
          We do not sell your personal information to third-party data brokers.
        </p>

        <h3>6. Data Security</h3>
        <p>
          We use reasonable technical and organizational measures—such as
          secure connections (HTTPS), access controls, and cloud security
          practices—to help protect your information. However, no system can be
          guaranteed to be 100% secure, and you use the Services at your own
          risk.
        </p>

        <h3>7. Data Retention</h3>
        <p>
          We retain information for as long as it is reasonably necessary to
          provide the Services, comply with our legal obligations, resolve
          disputes, and enforce our agreements. When information is no longer
          needed, we may delete or anonymize it in accordance with our data
          retention practices.
        </p>

        <h3>8. Your Choices & Rights</h3>
        <p>Depending on your location, you may have rights to:</p>
        <ul>
          <li>Access the information we hold about you.</li>
          <li>Request updates or corrections to your information.</li>
          <li>
            Request deletion of certain information, subject to legal or
            operational requirements.
          </li>
        </ul>
        <p>
          To make a request or ask questions about your data, you can contact us
          at{" "}
          <a href="mailto:contact@littlepawsplace.com">
            contact@littlepawsplace.com
          </a>
          .
        </p>

        <h3>9. Children’s Privacy</h3>
        <p>
          Our Services are not directed to children under 13, and we do not
          knowingly collect personal information from children under 13. If you
          believe we may have collected information from a child, please contact
          us so we can investigate and take appropriate action.
        </p>

        <h3>10. Changes to This Policy</h3>
        <p>
          We may update this Privacy Policy from time to time. When we do, we
          will update the “Last updated” date above. Your continued use of the
          Services after changes take effect means you accept the revised
          Policy.
        </p>

        <h3>11. Contact Us</h3>
        <p>
          If you have questions or concerns about this Privacy Policy, you can
          reach us at{" "}
          <a href="mailto:contact@littlepawsplace.com">
            contact@littlepawsplace.com
          </a>
          .
        </p>

        <p className="privacy-disclaimer">
          This Policy is intended as a general explanation of our practices and
          is not legal advice. If you need legal guidance, please consult a
          qualified professional.
        </p>

        <button className="privacy-back-button" onClick={goBack}>
          ← Go Back
        </button>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
